import { styled } from '@linaria/react';
import { Box, Flex, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { DeliveryImpactMetric } from '../../../api/agent-miner-client/agent-miner-client.type';
import { inkLight, secondaryBase, skyDark } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { H3, Text } from '../../../ui-library/typography/typography';
import { fmtMetric } from '../model-comparison/model-comparison.helpers';
import { deltaChipColor, formatDelta } from './delivery-impact.helpers';
import type { MetricCardConfig } from './delivery-impact.type';

type Props = {
  config: MetricCardConfig;
  metric: DeliveryImpactMetric | undefined;
};

/**
 * Width of a cohort's proportional bar, scaled so the larger side fills the
 * track. A null value renders an empty track rather than a zero-width sliver
 * being conflated with a real zero.
 *
 * @param {number | null | undefined} value - The cohort's metric value
 * @param {number} scaleMax - The larger of the two cohort values
 * @returns {number} Percentage width in [0, 100]
 */
const barWidthPct = (value: number | null | undefined, scaleMax: number): number => {
  if (value === null || value === undefined || scaleMax <= 0) return 0;
  return Math.min(100, (value / scaleMax) * 100);
};

/**
 * One Delivery Impact comparison card: the AI-assisted value against the
 * baseline value with a delta chip and two proportional bars. The delta chip's
 * color follows the metric's improvement direction; the neutral
 * (scope-context) card stays ink-colored.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The comparison card
 */
export const MetricComparisonCard = ({ config, metric }: Props) => {
  const { t } = useTranslation();
  const scaleMax = Math.max(metric?.ai ?? 0, metric?.baseline ?? 0);

  return (
    <Card data-testid={`delivery-impact-card-${config.key}`}>
      <Group gap={8} align="center">
        <Text size="regular" lineHeight="none">
          {t(config.labelKey)}
        </Text>
        <Tooltip
          label={t(config.infoKey)}
          position="top"
          variant="black"
          multiline
          maw={300}
          events={{ hover: true, focus: true, touch: false }}
        >
          <Box
            component="span"
            tabIndex={0}
            aria-label={t(config.infoKey)}
            style={{ display: 'inline-flex', cursor: 'help' }}
          >
            <Icon name="info" size={14} color={skyDark} />
          </Box>
        </Tooltip>
      </Group>

      <Group gap={8} align="baseline">
        <H3>{fmtMetric(metric?.ai, config.unit)}</H3>
        <Text size="small" color={inkLight}>
          {t('agent_miner.delivery_impact.vs_baseline', { baseline: fmtMetric(metric?.baseline, config.unit) })}
        </Text>
        <Text size="small" weight="bold" color={deltaChipColor(metric?.delta_pct, config.direction)}>
          {formatDelta(metric?.delta_pct)}
        </Text>
      </Group>

      <Flex direction="column" gap={6}>
        <BarRow>
          <BarLabel>
            <Text size="tiny" color={secondaryBase}>
              {t('agent_miner.delivery_impact.ai_label')}
            </Text>
          </BarLabel>
          <BarTrack>
            <BarFill $color={secondaryBase} style={{ width: `${barWidthPct(metric?.ai, scaleMax)}%` }} />
          </BarTrack>
        </BarRow>
        <BarRow>
          <BarLabel>
            <Text size="tiny" color={inkLight}>
              {t('agent_miner.delivery_impact.baseline_label')}
            </Text>
          </BarLabel>
          <BarTrack>
            <BarFill $color={skyDark} style={{ width: `${barWidthPct(metric?.baseline, scaleMax)}%` }} />
          </BarTrack>
        </BarRow>
      </Flex>
    </Card>
  );
};

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  border-radius: 8px;
  min-width: 0;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BarLabel = styled.div`
  width: 120px;
  flex-shrink: 0;
  /* Fits the longest cohort label ("Non-AI-linked tasks") on one line so the
     AI and baseline bar rows stay the same height and their tracks align. */
  white-space: nowrap;
`;

const BarTrack = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 99px;
  background: var(--sky-light);
  overflow: hidden;
`;

const BarFill = styled.div<{ $color: string }>`
  height: 100%;
  border-radius: 99px;
  background: ${({ $color }) => $color};
  transition: width 0.2s ease;
`;
